﻿import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  doc, getDoc, setDoc, collection,
  query, where, getDocs, serverTimestamp, onSnapshot, updateDoc, addDoc, limit,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { deleteFileByUrl } from '../lib/storage';
import { useAuth } from './AuthContext';
import { generateId } from '../lib/utils';
import { CARD_LIBRARY } from '../data/mockData';
import { omitBlockedWeddingKeys, overlayFinance } from '../lib/coupleFinance';

const WeddingContext = createContext(null);

const DEFAULT_WEDDING = {
  groomName:   '',
  brideName:   '',
  weddingDate: '',
  story:       '',
  pixKey:      '',
  slug:        '',
  themeId:     'classic',
  cards:       [],
  aiStyle:     'watercolor',
  humorLevel:  'cute',
  goalAmount:  10000,
  isPublished: false,
  rsvps:       [],
  pets:        [],
  // Tipografia customizada (opcional). Quando definida, sobrepõe a fonte
  // padrão do tema. Ids correspondem aos ids em src/data/fonts.js.
  // `story` é opcional — sem ele, o texto de "Nossa História" usa a mesma
  // fonte de `heading` (comportamento histórico).
  // Ex.: { heading: 'playfair', body: 'lato', story: 'lora' }
  customFonts: null,
};

const isValidImageUrl = (value) => {
  if (typeof value !== 'string') return false;
  const url = value.trim();
  if (!url) return false;
  if (url.startsWith('blob:')) return false;
  if (url.includes('undefined') || url.includes('null')) return false;
  return /^(https?:\/\/|gs:\/\/|\/)/i.test(url);
};

const sanitizePhotoArray = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr.filter(isValidImageUrl);
};

const sanitizeWeddingPhotos = (data = {}) => {
  const groomPhotos = sanitizePhotoArray(data.groomPhotos);
  const bridePhotos = sanitizePhotoArray(data.bridePhotos);
  const gallery = sanitizePhotoArray(data.gallery);
  const coverPhoto = isValidImageUrl(data.coverPhoto) ? data.coverPhoto : null;
  const couplePhoto = isValidImageUrl(data.couplePhoto) ? data.couplePhoto : null;
  const groomPhoto = isValidImageUrl(data.groomPhoto) ? data.groomPhoto : (groomPhotos[0] || null);
  const bridePhoto = isValidImageUrl(data.bridePhoto) ? data.bridePhoto : (bridePhotos[0] || null);

  const changed =
    coverPhoto !== (data.coverPhoto || null) ||
    couplePhoto !== (data.couplePhoto || null) ||
    groomPhoto !== (data.groomPhoto || null) ||
    bridePhoto !== (data.bridePhoto || null) ||
    JSON.stringify(groomPhotos) !== JSON.stringify(Array.isArray(data.groomPhotos) ? data.groomPhotos : []) ||
    JSON.stringify(bridePhotos) !== JSON.stringify(Array.isArray(data.bridePhotos) ? data.bridePhotos : []) ||
    JSON.stringify(gallery) !== JSON.stringify(Array.isArray(data.gallery) ? data.gallery : []);

  return {
    changed,
    cleaned: { coverPhoto, couplePhoto, groomPhoto, bridePhoto, groomPhotos, bridePhotos, gallery },
  };
};

export function WeddingProvider({ children }) {
  const { user }                            = useAuth();
  const [wedding, setWedding]               = useState(null);
  const [onboardingDone, setOnboardingDone] = useState(false);
  const [weddingLoading, setWeddingLoading] = useState(true);
  const [publicLibraryCards, setPublicLibraryCards] = useState([]);
  // Ref espelhando o wedding atual — evita closure stale em ações rápidas
  // em sequência (deletar/editar vários cards seguidos sem o React ter
  // re-renderizado entre eles).
  const weddingRef = useRef(null);
  const weddingDataRef = useRef(null);
  const financeDataRef = useRef(null);
  useEffect(() => { weddingRef.current = wedding; }, [wedding]);

  // Section
  // Depende somente do user.id pra evitar re-execuções a cada updateUser/
  // refresh de token (que mudam a referência do objeto user, mas mantêm o
  // mesmo usuário logado). Isso causava flash de /onboarding intermitente.
  const userId = user?.id || null;
  const userOnboardingFlag = !!user?.onboardingCompleted;
  useEffect(() => {
    if (!userId) {
      // Reset ao deslogar — sincronização legítima de state com auth externo.
      setWedding(null);
      setOnboardingDone(false);
      setWeddingLoading(false);
      return;
    }
    // Se já sabemos pelo user doc que o onboarding está completo, antecipa
    // o estado pra evitar redirect indevido enquanto o snapshot carrega.
    // Não reseta pra false aqui: deixa o snapshot decidir caso negativo.
    if (userOnboardingFlag) setOnboardingDone(true);
    setWeddingLoading(true);
    const ref = doc(db, 'weddings', userId);

    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          const { changed, cleaned } = sanitizeWeddingPhotos(data);
          const nextWedding = changed ? { ...data, ...cleaned } : data;
          weddingDataRef.current = nextWedding;
          // `rsvps` não mora no documento weddings/{userId} — é mesclado à
          // parte por um listener na coleção `rsvps` (efeito abaixo). Sem
          // preservar aqui, qualquer escrita no wedding (ex: adicionar
          // convidado) reexecuta este snapshot e apaga `rsvps` do estado até
          // a próxima mudança na coleção rsvps (ou um F5) trazer de volta.
          setWedding((prev) => ({
            ...overlayFinance(nextWedding, financeDataRef.current),
            rsvps: prev?.rsvps || [],
          }));
          if (changed) {
            updateDoc(ref, { ...cleaned, updatedAt: serverTimestamp() }).catch(() => {});
          }
          setOnboardingDone(!!(data.onboardingCompleted || (data.groomName && data.brideName) || userOnboardingFlag));
        } else {
          weddingDataRef.current = null;
          setWedding({ ...DEFAULT_WEDDING });
          setOnboardingDone(userOnboardingFlag);
        }
        setWeddingLoading(false);
      },
      () => {
        // Section
        setWedding({ ...DEFAULT_WEDDING });
        setOnboardingDone(userOnboardingFlag);
        setWeddingLoading(false);
      }
    );

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;
    const unsub = onSnapshot(doc(db, 'weddingFinance', userId), (snap) => {
      financeDataRef.current = snap.exists() ? snap.data() : null;
      const base = weddingDataRef.current;
      if (!base) return;
      setWedding((prev) => ({
        ...overlayFinance(base, financeDataRef.current),
        rsvps: prev?.rsvps || [],
      }));
    }, () => {});
    return unsub;
  }, [userId]);

  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'rsvps'),
      where('userId', '==', user.id),
      limit(500)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      const items = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const aTs = a?.createdAt?.seconds || a?.updatedAt?.seconds || 0;
          const bTs = b?.createdAt?.seconds || b?.updatedAt?.seconds || 0;
          return bTs - aTs;
        });
      setWedding((prev) => (prev ? { ...prev, rsvps: items } : prev));
    }, () => {});
    return unsubscribe;
  }, [user]);

  useEffect(() => {
    // Biblioteca = 30 cards built-in + cards aprovados pelo admin no Firestore.
    // Dedup por título (case-insensitive) caso o admin aprove um similar.
    const mergeWithBuiltins = (firestoreCards) => {
      const byTitle = new Map();
      for (const c of CARD_LIBRARY) byTitle.set(c.title.toLowerCase(), c);
      for (const c of firestoreCards) byTitle.set(String(c.title || '').toLowerCase(), c);
      return Array.from(byTitle.values());
    };
    const q = query(collection(db, 'publicCardLibrary'), limit(300));
    const unsubscribe = onSnapshot(q, (snap) => {
      const firestoreCards = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((card) => card.isActive !== false);
      setPublicLibraryCards(mergeWithBuiltins(firestoreCards));
    }, () => {
      setPublicLibraryCards(mergeWithBuiltins([]));
    });
    return unsubscribe;
  }, []);

  // Section
  const saveWedding = useCallback(async (updates) => {
    if (!user) return;
    const current = weddingRef.current || {};
    const safeUpdates = omitBlockedWeddingKeys(updates);
    const payload = { ...safeUpdates, updatedAt: serverTimestamp() };
    // Atualiza a ref de imediato p/ que chamadas em rajada já vejam o
    // novo estado, sem precisar esperar o setWedding re-renderizar.
    weddingRef.current = { ...current, ...safeUpdates };
    if (weddingDataRef.current) weddingDataRef.current = { ...weddingDataRef.current, ...safeUpdates };
    await setDoc(doc(db, 'weddings', user.id), payload, { merge: true });
    setWedding(prev => ({ ...prev, ...safeUpdates }));
  }, [user]);

  const refreshWedding = useCallback(async () => {
    if (!user) return null;
    const ref = doc(db, 'weddings', user.id);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const data = snap.data();
    const { changed, cleaned } = sanitizeWeddingPhotos(data);
    const nextWedding = overlayFinance(changed ? { ...data, ...cleaned } : data, financeDataRef.current);
    weddingDataRef.current = changed ? { ...data, ...cleaned } : data;
    weddingRef.current = nextWedding;
    // Mesma ressalva do listener principal: `rsvps` não vem do documento
    // weddings/{userId}, preserva o que já estava no estado.
    setWedding((prev) => ({ ...nextWedding, rsvps: prev?.rsvps || [] }));
    if (changed) {
      updateDoc(ref, { ...cleaned, updatedAt: serverTimestamp() }).catch(() => {});
    }
    return nextWedding;
  }, [user]);

  // Section
  const addCard = useCallback(async (card) => {
    const newCard = {
      ...card,
      id:        generateId(),
      createdAt: new Date().toISOString(),
      purchases: 0,
      maxQuantity: Number.isInteger(card?.maxQuantity) ? card.maxQuantity : 5,
      hidden:    false,
      featured:  false,
    };
    const currentCards = weddingRef.current?.cards || [];
    // firstCardAddedAt marca a transição 0→1 presente — sinal de ativação
    // real, usado pelo lembrete de lista vazia (dispara só quem nunca
    // passou por aqui).
    const updates = { cards: [...currentCards, newCard] };
    if (currentCards.length === 0 && !weddingRef.current?.firstCardAddedAt) {
      updates.firstCardAddedAt = serverTimestamp();
    }
    await saveWedding(updates);
    return newCard;
  }, [saveWedding]);

  const updateCard = useCallback(async (id, updates) => {
    const currentCards = weddingRef.current?.cards || [];
    const cards = currentCards.map(c => c.id === id ? { ...c, ...updates } : c);
    await saveWedding({ cards });
  }, [saveWedding]);

  const deleteCard = useCallback(async (id) => {
    const currentCards = weddingRef.current?.cards || [];
    const removed = currentCards.find((c) => c.id === id);
    await saveWedding({ cards: currentCards.filter(c => c.id !== id) });
    // Só apaga do Storage se a imagem foi enviada pelo próprio casal (não
    // toca em imagens da biblioteca de presentes, que são compartilhadas).
    const img = removed?.image;
    if (user?.id && typeof img === 'string' && img.includes(`weddings%2F${user.id}%2Fcards%2F`)) {
      deleteFileByUrl(img);
    }
  }, [saveWedding, user]);

  const reorderCards = useCallback(async (newOrder) => {
    await saveWedding({ cards: newOrder });
  }, [saveWedding]);

  // Section
  const completeOnboarding = useCallback(async (data) => {
    if (!user) throw new Error('Sessão não está pronta. Recarregue a página e tente novamente.');
    // Código único de referral — derivado do slug + uid pra ficar bonito de ler
    // e único o suficiente pra evitar colisões.
    const { buildReferralCode } = await import('../lib/referral');
    const referralCode = buildReferralCode({ slug: data.slug, uid: user.id });
    const { buildLeadFields } = await import('../lib/leadQuality');
    const lead = buildLeadFields({ weddingDate: data.weddingDate, weddingDateStatus: data.weddingDateStatus });
    const weddingData = {
      ...DEFAULT_WEDDING,
      ...data,
      weddingDateStatus: lead.weddingDateStatus,
      leadQualityAtOnboarding: lead.lead_quality,
      monthsToWeddingAtOnboarding: lead.months_to_wedding,
      ...(lead.lead_quality === 'qualificado' ? { leadQualifiedAt: new Date() } : {}),
      userId:      user.id,
      onboardingCompleted: true,
      isPublished: true,
      referralCode,
      createdAt:   serverTimestamp(),
      updatedAt:   serverTimestamp(),
    };

    // Retry resiliente para a primeira escrita: em fluxo signup → onboarding
    // o token de auth pode levar um momento para propagar ao Firestore.
    const trySetDoc = async (ref, value, attempts = 3) => {
      let lastErr = null;
      for (let i = 0; i < attempts; i += 1) {
        try {
          await setDoc(ref, value);
          return;
        } catch (err) {
          lastErr = err;
          if (i < attempts - 1) {
            await new Promise((r) => setTimeout(r, 600 * (i + 1)));
          }
        }
      }
      throw lastErr;
    };

    const toSave = omitBlockedWeddingKeys(weddingData);
    await trySetDoc(doc(db, 'weddings', user.id), toSave);

    if (data.slug) {
      await trySetDoc(doc(db, 'slugs', data.slug), {
        userId:    user.id,
        createdAt: serverTimestamp(),
      });
    }
    await updateDoc(doc(db, 'users', user.id), {
      onboardingCompleted: true,
      updatedAt: serverTimestamp(),
    }).catch(() => {});

    setWedding(toSave);
    setOnboardingDone(true);
  }, [user]);

  // Section
  const getPublicWedding = useCallback(async (slug) => {
    try {
      const slugSnap = await getDoc(doc(db, 'slugs', slug));
      if (slugSnap.exists() && slugSnap.data()?.userId) {
        try {
          const pubSnap = await getDoc(doc(db, 'weddingPublic', slugSnap.data().userId));
          if (pubSnap.exists()) return pubSnap.data();
        } catch { /* regras novas: segue para a function */ }
      }
      try {
        const snap = await getDocs(query(
          collection(db, 'weddingPublic'),
          where('slug', '==', slug),
          where('isPublished', '==', true),
          limit(1),
        ));
        if (!snap.empty) return snap.docs[0].data();
      } catch { /* a lista pode falhar; a function ainda monta a vitrine */ }
      // Sem vitrine espelhada (janela da migração ou doc que falhou).
      // A function lê o casamento no servidor e devolve só a projeção.
      const resp = await fetch(`/api/publicWedding?slug=${encodeURIComponent(slug)}`);
      if (!resp.ok) return null;
      const json = await resp.json().catch(() => ({}));
      return json.wedding || null;
    } catch {
      return null;
    }
  }, []);

  const addPendingLibraryCards = useCallback(async (cards = []) => {
    if (!user?.id) throw new Error('Usuário não autenticado para enviar moderação.');
    const normalized = cards
      .filter((c) => c?.title && c?.image)
      .map((c) => ({
        title: c.title,
        description: c.description || '',
        category: c.category || 'couple',
        suggestedValue: Number(c.value || c.suggestedValue || 100),
        image: c.image,
        source: 'ai-no-face',
        status: 'pending',
        submittedBy: user.uid || user.id,
        createdAt: serverTimestamp(),
      }));
    if (!normalized.length) return;
    await Promise.all(normalized.map((card) => addDoc(collection(db, 'aiCardSubmissions'), card)));
  }, [user]);

  return (
    <WeddingContext.Provider value={{
      wedding, saveWedding, refreshWedding, addCard, updateCard, deleteCard, reorderCards,
      onboardingDone, completeOnboarding, getPublicWedding, weddingLoading,
      publicLibraryCards, addPendingLibraryCards,
    }}>
      {children}
    </WeddingContext.Provider>
  );
}

export const useWedding = () => {
  const ctx = useContext(WeddingContext);
  if (!ctx) throw new Error('useWedding must be used within WeddingProvider');
  return ctx;
};
